/**
 * Code Value Model
 * Matches: code_value table from database DDL (v5)
 *
 * Centralized lookup table for all enumerated values.
 * Data tables store the code (string), this table provides:
 *   - Display labels (supports localization)
 *   - Sort order for dropdowns
 *   - Enable/disable options
 *   - Tenant-specific customization
 *   - Metadata (colors, icons, etc.)
 */

// =============================================================================
// Code Value Categories
// =============================================================================

/**
 * Known code value categories
 */
export type CodeCategory =
  // System
  | 'TENANT_STATUS'
  | 'TENANT_SUBSCRIPTION_STATUS'
  | 'USER_STATUS'
  // Organization
  | 'ORG_TYPE'
  | 'ORG_STRUCTURE'
  | 'ORG_STATUS'
  | 'ORG_RELATIONSHIP_TYPE'
  | 'ADDRESS_TYPE'
  | 'COUNTRY'
  | 'US_STATE'
  | `${string}_STATE`
  // Membership & Capacity
  | 'MEMBERSHIP_STATUS'
  | 'MEMBERSHIP_SUBSCRIPTION_STATUS'
  | 'CAPACITY_TYPE'
  // Per-capacity-type status categories: CAPACITY_STATUS_<TYPE CODE>. Template
  // literal so tenant-defined capacity types resolve too, not just the seeds
  // (mirrors the backend MemboraApiConstants.Categories.CapacityStatusFor).
  | `CAPACITY_STATUS_${string}`
  | 'CONTACT_TYPE'
  // CRM - Pipeline
  | 'PIPELINE_SCOPE_TYPE'
  | 'PIPELINE_APPLIES_TO'
  | 'PIPELINE_VISIBILITY'
  | 'PIPELINE_ITEM_STATUS'
  | 'PIPELINE_TERMINAL_STAGE'
  // CRM - Activities & Tasks
  | 'ACTIVITY_TYPE'
  | 'ACTIVITY_VISIBILITY'
  | 'TASK_STATUS'
  | 'TASK_PRIORITY'
  // Events
  | 'EVENT_TYPE'
  | 'EVENT_FORMAT'
  | 'EVENT_STATUS'
  | 'EVENT_VISIBILITY'
  | 'EVENT_REGISTRATION_STATUS'
  | 'EVENT_REGISTRATION_TYPE'
  | 'PAYMENT_STATUS'
  // Discussions
  | 'THREAD_TYPE'
  | 'THREAD_STATUS'
  | 'THREAD_VISIBILITY'
  // Resources
  | 'RESOURCE_TYPE'
  | 'RESOURCE_STATUS'
  | 'RESOURCE_VISIBILITY'
  | 'RESOURCE_PERMISSION'
  // CMS
  | 'CMS_CONTENT_STATUS'
  | 'CMS_VISIBILITY'
  // Billing / SaaSLogic
  | 'SAASLOGIC_TAX_EXEMPTION_REASON'
  // Storefront (V073/V074)
  | 'STORE_PRODUCT_STATUS'
  | 'STORE_PRODUCT_TYPE'
  | 'STORE_PAYMENT_STATUS'
  | 'STORE_SHIPPING_STATUS'
  | 'STORE_ORDER_SOURCE'
  | 'STORE_COUPON_TYPE'
  | 'STORE_COUPON_RESTRICT_TYPE'
  | 'STORE_SHIPPING_ZONE'
  | 'CARRIER'
  | 'CURRENCY';

// =============================================================================
// Code Value Interface
// =============================================================================

/**
 * Metadata stored in the code_value.metadata JSON column
 */
export interface CodeValueMetadata {
  color?: string;           // Color name or hex
  variant?: string;         // Badge variant: 'success', 'warning', 'error', 'info', 'default'
  icon?: string;            // Icon name
  cssClass?: string;        // CSS class to apply
  [key: string]: unknown;   // Allow additional metadata
}

/**
 * Code value record from the database
 */
export interface CodeValue {
  tenantId: string | null;  // NULL = system default
  category: CodeCategory;
  code: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  isSystem: boolean;        // System values can't be deleted
  metadata: CodeValueMetadata;
  createdAt: string;
  updatedAt: string;
}

/**
 * Simplified code value for dropdowns
 */
export interface CodeValueOption {
  code: string;
  name: string;
  description?: string;
  metadata?: CodeValueMetadata;
}

// =============================================================================
// DTOs
// =============================================================================

/**
 * Create a new tenant-specific code value
 */
export interface CreateCodeValueDto {
  category: CodeCategory;
  code: string;
  name: string;
  description?: string;
  sortOrder?: number;
  metadata?: CodeValueMetadata;
}

/**
 * Update an existing code value
 */
export interface UpdateCodeValueDto {
  category: CodeCategory;
  code: string;
  name?: string;
  description?: string;
  sortOrder?: number;
  isActive?: boolean;
  metadata?: CodeValueMetadata;
}

// =============================================================================
// Query Parameters
// =============================================================================

export interface CodeValueQueryParams {
  category?: CodeCategory;
  includeInactive?: boolean;
  includeSystemDefaults?: boolean;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Get the display name for a code from a list of code values
 */
export function getCodeName(
  codeValues: CodeValue[] | CodeValueOption[],
  code: string
): string {
  const found = codeValues.find(cv => cv.code === code);
  return found?.name ?? code;
}

/**
 * Get metadata for a code from a list of code values
 */
export function getCodeMetadata(
  codeValues: CodeValue[],
  code: string
): CodeValueMetadata | undefined {
  const found = codeValues.find(cv => cv.code === code);
  return found?.metadata;
}

/**
 * Convert code values to select options (for dropdowns)
 */
export function toSelectOptions(
  codeValues: CodeValue[] | CodeValueOption[]
): { value: string; label: string }[] {
  return codeValues.map(cv => ({
    value: cv.code,
    label: cv.name,
  }));
}

/**
 * Sort code values by sortOrder
 */
export function sortCodeValues<T extends { sortOrder?: number }>(values: T[]): T[] {
  return [...values].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}
